/**
 * Timezone utilities for user-timezone-aware date handling
 * 
 * ARCHITECTURE:
 * - UI components emit intent (strings or {date, timezone} objects)
 * - filterPillsToAST normalizes ONCE to canonical {date, timezone} shape
 * - After normalization, timezone is passed explicitly as data, never detected again
 * - Backend compiler expands {date, timezone} to UTC instants
 */

/**
 * Get the user's timezone from browser
 * Falls back to UTC if detection fails
 * 
 * This is ONLY called in filterPillsToAST for normalization.
 * After normalization, timezone is passed explicitly as data.
 * 
 * @returns {string} IANA timezone identifier (e.g., "Asia/Kolkata", "America/New_York")
 */
export function getUserTimezone() {
  try {
    const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    if (timezone) {
      return timezone;
    }
  } catch (error) {
    console.warn('Failed to detect timezone, falling back to UTC:', error);
  }
  return 'UTC';
}

/**
 * Normalize a date value to the canonical shape: {date: string, timezone: string}
 * 
 * THIS IS THE ONLY NORMALIZATION POINT.
 * Called exclusively by filterPillsToAST at the AST boundary.
 * 
 * @param {any} value - Date value (string or {date, timezone} object)
 * @param {string} defaultTimezone - Timezone to use if value is a string
 * @returns {{date: string, timezone: string}} Normalized date value
 * @throws {Error} If value cannot be normalized
 */
export function normalizeDateValue(value, defaultTimezone) {
  if (!value) {
    throw new Error('Cannot normalize null or undefined date value');
  }
  
  // Already in canonical shape - validate and return
  if (typeof value === 'object' && value !== null && value.date && value.timezone) {
    // Validate shape
    if (typeof value.date !== 'string' || !/^\d{4}-\d{2}-\d{2}$/.test(value.date)) {
      throw new Error(`Invalid date format: "${value.date}". Expected YYYY-MM-DD.`);
    }
    if (typeof value.timezone !== 'string' || value.timezone.length === 0) {
      throw new Error(`Invalid timezone: "${value.timezone}". Expected IANA timezone identifier.`);
    }
    return value;
  }
  
  // Legacy string format - wrap with default timezone
  if (typeof value === 'string') {
    if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) {
      throw new Error(`Invalid date string format: "${value}". Expected YYYY-MM-DD.`);
    }
    return {
      date: value,
      timezone: defaultTimezone
    };
  }
  
  throw new Error(
    `Cannot normalize date value: expected string or {date, timezone} object, ` +
    `got ${typeof value}`
  );
}

